import React, { useState, useMemo, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Grid,
    TextField,
    Divider,
    MenuItem
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import { t } from 'i18next';
import { useUser } from '../../../hooks/useUser';
import { useCreateStressTest, useUploadStressTestFile } from 'hooks/useStressTest';

export const MEDICAL_TEST_TYPES = {
    UNKNOWN: 'UNKNOWN',

    STRESS_TEST: 'STRESS_TEST',
    STRESS_TEST_CP: 'STRESS_TEST_CP',

    ECG_1L: 'ECG_1L',
    ECG_12L: 'ECG_12L',
    ECG_HOLTER: 'ECG_HOLTER',

    ECO_CG: 'ECO_CG',
    ECO_CG_DOPPLER: 'ECO_CG_DOPPLER',

    ABPM: 'ABPM',
    PSG: 'PSG',
    BIOCHEMISTRY: 'BIOCHEMISTRY',
    OTHER: 'OTHER'
};

const ECG_TYPES = [
    MEDICAL_TEST_TYPES.ECG_1L,
    MEDICAL_TEST_TYPES.ECG_12L,
    MEDICAL_TEST_TYPES.ECG_HOLTER
];

const OTHER_TEST_TYPES = [
    MEDICAL_TEST_TYPES.ECO_CG,
    MEDICAL_TEST_TYPES.ECO_CG_DOPPLER,
    MEDICAL_TEST_TYPES.ABPM,
    MEDICAL_TEST_TYPES.PSG,
    MEDICAL_TEST_TYPES.BIOCHEMISTRY,
    MEDICAL_TEST_TYPES.OTHER
];

const MEDICAL_TEST_LABELS = {
    ECG_1L: 'ECG 1 derivação',
    ECG_12L: 'ECG 12 derivações',
    ECG_HOLTER: 'Holter ECG',

    ECO_CG: 'Ecocardiograma',
    ECO_CG_DOPPLER: 'Ecocardiograma Doppler',

    ABPM: 'MAPA (ABPM)',
    PSG: 'Polissonografia',

    BIOCHEMISTRY: 'Análises Bioquímicas',
    OTHER: 'Outro'
};

const calculateAge = (birthDate) => {
    if (!birthDate) return '';
    const today = new Date();
    const birth = new Date(birthDate);

    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();

    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
        age--;
    }

    return age;
};

const calculateBMI = (weight, height) => {
    if (!weight || !height) return '';
    const h = height / 100;
    return (weight / (h * h)).toFixed(1);
};

const CreateMedicalTestPage = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const from = state.from;
    const seeUserState = state.seeUserState;
    const { user } = useUser();

    const patient = state?.patient;
    const category = state?.category;

    const { mutate, isPending } = useCreateStressTest();

    const { mutate: uploadFile, isPending: isUploading } = useUploadStressTestFile();

    const [identification, setIdentification] = useState({
        name: patient?.name || '',
        sex: patient?.sex || '',
        birthdate: patient?.birth_date || '',
        age: calculateAge(patient?.birth_date),
        weight: patient?.weight || '',
        height: patient?.height || '',
        bmi: calculateBMI(patient?.weight, patient?.height),
        processNumber: patient?.health_id || ''
    });

    const [generalData, setGeneralData] = useState({
        origin: user?.selectedClinic?.name || '',
        doctor: user?.name || '',
        operators: ''
    });

    const [clinicalInfo, setClinicalInfo] = useState('');

    const availableTypes = useMemo(
        () => (category === 'ECG' ? ECG_TYPES : OTHER_TEST_TYPES),
        [category]
    );

    const [selectedType, setSelectedType] = useState('');

    const [file, setFile] = useState(null);

    const [consultationNotes, setConsultationNotes] = useState('');

    useEffect(() => {
        setIdentification((prev) => ({
            ...prev,
            bmi: calculateBMI(prev.weight, prev.height)
        }));
    }, [identification.weight, identification.height]);

    if (!patient || !category) {
        return <Typography>{t('exams.invalidData')}</Typography>;
    }

    const handleCreate = () => {
        mutate(
            {
                patient: patient.role_uuid,
                doctor: user?.selectedRole,
                caregiver: null,
                type: selectedType,
                ts: dayjs().toISOString(),
                data: {
                    identification,
                    generalData,
                    clinicalInfo,
                    consultationNotes
                }
            },
            {
                onSuccess: (resp) => {
                    const testUuid = resp.data.uuid;

                    if (file) {
                        uploadFile(
                            {
                                testUuid,
                                file
                            },
                            {
                                onSuccess: () =>
                                    navigate(from.pathname, {
                                        state: {
                                            ...from.state,
                                            restoredState: seeUserState
                                        }
                                    })
                            }
                        );
                    } else {
                        navigate(from.pathname, {
                            state: {
                                ...from.state,
                                restoredState: seeUserState
                            }
                        });
                    }
                }
            }
        );
    };

    const handleBack = () => {
        if (from) {
            navigate(from.pathname, {
                state: {
                    ...from.state,
                    restoredState: seeUserState
                }
            });
        } else {
            navigate(-1);
        }
    };

    return (
        <Box mt={2}>
            <Card>
                <CardContent sx={{ backgroundColor: '#f9f9f9' }}>
                    <Typography variant="h5" fontWeight={700} mb={1}>
                        Novo {category === 'ECG' ? 'ECG' : 'Exame'}
                    </Typography>

                    <Box
                        sx={{
                            height: 3,
                            width: 80,
                            backgroundColor: '#1976d2',
                            borderRadius: 2,
                            mb: 3
                        }}
                    />

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                select
                                fullWidth
                                label={t('stress.type')}
                                value={selectedType}
                                onChange={(e) => setSelectedType(e.target.value)}
                            >
                                {availableTypes.map((type) => (
                                    <MenuItem key={type} value={type}>
                                        {MEDICAL_TEST_LABELS[type]}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="h5">
                                {t('stress.identification')}
                            </Typography>
                        </Grid>

                        {Object.entries(identification).map(([key, value]) => (
                            <Grid item xs={12} md={4} key={key}>
                                <TextField
                                    fullWidth
                                    label={key}
                                    value={value}
                                    disabled={
                                        key === 'name' ||
                                        key === 'sex' ||
                                        key === 'birthdate' ||
                                        key === 'age' ||
                                        key === 'bmi'
                                    }
                                    onChange={(e) =>
                                        setIdentification((prev) => ({
                                            ...prev,
                                            [key]: e.target.value
                                        }))
                                    }
                                />
                            </Grid>
                        ))}

                        <Grid item xs={12}>
                            <Typography variant="h5">{t('stress.data')}</Typography>
                        </Grid>

                        {Object.entries(generalData).map(([key, value]) => (
                            <Grid item xs={12} md={4} key={key}>
                                <TextField
                                    fullWidth
                                    label={key}
                                    value={value}
                                    onChange={(e) =>
                                        setGeneralData((prev) => ({
                                            ...prev,
                                            [key]: e.target.value
                                        }))
                                    }
                                />
                            </Grid>
                        ))}

                        <Grid item xs={12}>
                            <Typography variant="h5">{t('stress.info')}</Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                label={t('stress.indication')}
                                value={clinicalInfo}
                                onChange={(e) => setClinicalInfo(e.target.value)}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="h5">{t('nutrition.notes')}</Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                minRows={6}
                                label={t('exams.notes')}
                                value={consultationNotes}
                                onChange={(e) => setConsultationNotes(e.target.value)}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="h5">{t('exams.doc')}</Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Button variant="outlined" component="label" fullWidth>
                                {t('exams.selectPDF')}
                                <input
                                    type="file"
                                    hidden
                                    accept="application/pdf"
                                    onChange={(e) => setFile(e.target.files[0])}
                                />
                            </Button>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                {file ? file.name : t('exams.noFileSelected')}
                            </Typography>
                        </Grid>
                    </Grid>

                    <Box display="flex" justifyContent="flex-end" mt={4} gap={2}>
                        <Button variant="outlined" onClick={handleBack}>
                            {t('stress.cancel')}
                        </Button>

                        <Button
                            variant="contained"
                            onClick={handleCreate}
                            disabled={isPending || isUploading || !selectedType}
                        >
                            {isPending || isUploading
                                ? t('exams.processing')
                                : t('stress.create')}
                        </Button>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

export default CreateMedicalTestPage;
